import { useQuery } from '@tanstack/react-query';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { ExternalLink } from 'lucide-react';
import { useNotionTaskModal } from '@/contexts/NotionTaskModalContext';
import { kanbanService } from '@/features/kanban/services/kanban.service';
import { NotionBlockRenderer } from './NotionBlockRenderer';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

export function NotionTaskDetailModal() {
  const { isOpen, pageId, notionLink, closeModal } = useNotionTaskModal();

  const { data: taskDetails, isLoading, error } = useQuery({
    queryKey: ['notion-task-details', pageId],
    queryFn: () => {
      if (!pageId) throw new Error('Page ID is required');
      return kanbanService.getTaskDetails(pageId);
    },
    enabled: isOpen && !!pageId,
    retry: 1,
  });

  const handleOpenInNotion = () => {
    if (notionLink) {
      window.open(notionLink, '_blank', 'noopener,noreferrer');
    } else if (pageId) {
      // Fallback: construir URL do Notion a partir do pageId
      const notionUrl = `https://notion.so/${pageId.replace(/-/g, '')}`;
      window.open(notionUrl, '_blank', 'noopener,noreferrer');
    }
  };

  // Ordem prioritária das propriedades
  const getPropertyPriority = (key: string): number => {
    const normalizedKey = key.toLowerCase().trim();

    // 1. Nome/Título
    if (
      normalizedKey.includes('nome') ||
      normalizedKey.includes('name') ||
      normalizedKey.includes('título') ||
      normalizedKey.includes('title')
    ) {
      return 1;
    }

    // 2. Status
    if (normalizedKey.includes('status')) {
      return 2;
    }

    // 3. Tipo de post e Coluna (mesma prioridade para ficarem lado a lado)
    if (
      normalizedKey.includes('tipo de post') ||
      normalizedKey.includes('tipo de post') ||
      normalizedKey.includes('posttype') ||
      normalizedKey.includes('post type') ||
      normalizedKey.includes('tipo post')
    ) {
      return 3;
    }

    if (
      normalizedKey.includes('coluna') ||
      normalizedKey.includes('column') ||
      normalizedKey === 'coluna' ||
      normalizedKey === 'column'
    ) {
      return 3;
    }

    // 4. Responsável de texto e Texto (checkbox) - mesma prioridade para ficarem lado a lado
    if (
      normalizedKey.includes('responsável de texto') ||
      normalizedKey.includes('responsavel de texto') ||
      normalizedKey.includes('responsibletext') ||
      normalizedKey.includes('responsável texto') ||
      normalizedKey.includes('responsavel texto')
    ) {
      return 4;
    }

    if (
      normalizedKey === 'texto' ||
      normalizedKey.includes('textcheckbox') ||
      normalizedKey.includes('text checkbox')
    ) {
      return 4;
    }

    // 5. Responsável de arte e Arte (checkbox) - mesma prioridade para ficarem lado a lado
    if (
      normalizedKey.includes('responsável de arte') ||
      normalizedKey.includes('responsavel de arte') ||
      normalizedKey.includes('responsibleart') ||
      normalizedKey.includes('responsável arte') ||
      normalizedKey.includes('responsavel arte')
    ) {
      return 5;
    }

    if (
      normalizedKey === 'arte' ||
      normalizedKey.includes('artcheckbox') ||
      normalizedKey.includes('art checkbox')
    ) {
      return 5;
    }

    // 6. Data de entrega
    if (
      normalizedKey.includes('data de entrega') ||
      normalizedKey.includes('duedate') ||
      normalizedKey.includes('due date') ||
      normalizedKey.includes('entrega')
    ) {
      return 6;
    }

    // 7. Aprovação do cliente
    if (
      normalizedKey.includes('aprovacao do cliente') ||
      normalizedKey.includes('aprovação do cliente') ||
      normalizedKey.includes('clientapproval') ||
      normalizedKey.includes('client approval')
    ) {
      return 7;
    }

    // 8. Agendamento
    if (normalizedKey.includes('agendamento') || normalizedKey.includes('scheduling')) {
      return 8;
    }

    // 9. Data de publicação
    if (
      normalizedKey.includes('data de publicação') ||
      normalizedKey.includes('data de publicacao') ||
      normalizedKey.includes('publicationdate') ||
      normalizedKey.includes('publication date') ||
      normalizedKey.includes('publicação')
    ) {
      return 10;
    }

    // Outras propriedades no final
    return 999;
  };

  const sortProperties = (properties: Record<string, any>): Array<[string, any]> => {
    return Object.entries(properties).sort(([keyA], [keyB]) => {
      const priorityA = getPropertyPriority(keyA);
      const priorityB = getPropertyPriority(keyB);
      if (priorityA !== priorityB) {
        return priorityA - priorityB;
      }
      // Se mesma prioridade, ordenar alfabeticamente
      return keyA.localeCompare(keyB);
    });
  };

  const renderProperty = (key: string, value: any) => {
    if (!value) return null;

    const propertyType = value.type;
    const propertyValue = value[propertyType];

    switch (propertyType) {
      case 'title':
        return (
          <div key={key} className="mb-3">
            <span className="text-xs font-medium text-muted-foreground uppercase">{key}:</span>
            <p className="text-sm text-foreground mt-1">
              {propertyValue
                ?.map((text: any) => text.plain_text)
                .join('') || '—'}
            </p>
          </div>
        );

      case 'rich_text':
        return (
          <div key={key} className="mb-3">
            <span className="text-xs font-medium text-muted-foreground uppercase">{key}:</span>
            <p className="text-sm text-foreground mt-1">
              {propertyValue
                ?.map((text: any) => text.plain_text)
                .join('') || '—'}
            </p>
          </div>
        );

      case 'number':
        return (
          <div key={key} className="mb-3">
            <span className="text-xs font-medium text-muted-foreground uppercase">{key}:</span>
            <p className="text-sm text-foreground mt-1">{propertyValue ?? '—'}</p>
          </div>
        );

      case 'select':
        return propertyValue ? (
          <div key={key} className="mb-3">
            <span className="text-xs font-medium text-muted-foreground uppercase">{key}:</span>
            <div className="mt-1">
              <Badge variant="outline" className="text-xs">
                {propertyValue.name}
              </Badge>
            </div>
          </div>
        ) : null;

      case 'multi_select':
        return propertyValue && propertyValue.length > 0 ? (
          <div key={key} className="mb-3">
            <span className="text-xs font-medium text-muted-foreground uppercase">{key}:</span>
            <div className="flex flex-wrap gap-1 mt-1">
              {propertyValue.map((item: any, index: number) => (
                <Badge key={index} variant="outline" className="text-xs">
                  {item.name}
                </Badge>
              ))}
            </div>
          </div>
        ) : null;

      case 'date': {
        if (!propertyValue) return null;
        const dateValue = propertyValue.start;
        if (!dateValue) return null;
        try {
          const date = new Date(dateValue);
          return (
            <div key={key} className="mb-3">
              <span className="text-xs font-medium text-muted-foreground uppercase">{key}:</span>
              <p className="text-sm text-foreground mt-1">
                {format(date, 'dd/MM/yyyy', { locale: ptBR })}
                {propertyValue.end && (
                  <span className="text-muted-foreground">
                    {' - '}
                    {format(new Date(propertyValue.end), 'dd/MM/yyyy', { locale: ptBR })}
                  </span>
                )}
              </p>
            </div>
          );
        } catch {
          return null;
        }
      }

      case 'checkbox':
        return (
          <div key={key} className="mb-3">
            <div className="flex items-center gap-2 mt-1">
              <Checkbox checked={propertyValue} disabled />
              <span className="text-xs font-medium text-muted-foreground uppercase">{key}</span>
            </div>
          </div>
        );

      case 'people':
        return propertyValue && propertyValue.length > 0 ? (
          <div key={key} className="mb-3">
            <span className="text-xs font-medium text-muted-foreground uppercase">{key}:</span>
            <div className="flex flex-wrap gap-1 mt-1">
              {propertyValue.map((person: any, index: number) => (
                <Badge key={index} variant="secondary" className="text-xs">
                  {person.name || person.id}
                </Badge>
              ))}
            </div>
          </div>
        ) : null;

      case 'files':
        return propertyValue && propertyValue.length > 0 ? (
          <div key={key} className="mb-3">
            <span className="text-xs font-medium text-muted-foreground uppercase">{key}:</span>
            <div className="space-y-1 mt-1">
              {propertyValue.map((file: any, index: number) => {
                const fileUrl = file.type === 'external' ? file.external?.url : file.file?.url;
                const fileName = file.name || 'Arquivo';
                return (
                  <div key={index} className="text-sm">
                    <a
                      href={fileUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary hover:underline"
                    >
                      {fileName}
                    </a>
                  </div>
                );
              })}
            </div>
          </div>
        ) : null;

      case 'url':
        return propertyValue ? (
          <div key={key} className="mb-3">
            <span className="text-xs font-medium text-muted-foreground uppercase">{key}:</span>
            <div className="mt-1">
              <a
                href={propertyValue}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-primary hover:underline"
              >
                {propertyValue}
              </a>
            </div>
          </div>
        ) : null;

      case 'email':
        return propertyValue ? (
          <div key={key} className="mb-3">
            <span className="text-xs font-medium text-muted-foreground uppercase">{key}:</span>
            <div className="mt-1">
              <a
                href={`mailto:${propertyValue}`}
                className="text-sm text-primary hover:underline"
              >
                {propertyValue}
              </a>
            </div>
          </div>
        ) : null;

      default:
        return null;
    }
  };

  if (error) {
    return (
      <Dialog open={isOpen} onOpenChange={closeModal}>
        <DialogContent size="lg" aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle>Erro ao carregar tarefa</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-destructive">
              Não foi possível carregar os detalhes da tarefa. Tente novamente.
            </p>
            {notionLink && (
              <Button
                variant="outline"
                className="mt-4"
                onClick={handleOpenInNotion}
              >
                <ExternalLink className="h-4 w-4 mr-2" />
                Abrir no Notion
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={closeModal}>
      <DialogContent size="lg" aria-describedby={undefined}>
        <DialogHeader>
          <div className="flex items-start justify-between gap-4">
            <DialogTitle className="flex-1">
              {isLoading ? (
                'Carregando...'
              ) : taskDetails ? (
                (() => {
                  // Tentar encontrar o título nas propriedades
                  const titleProperty = Object.entries(taskDetails.properties || {}).find(
                    ([, prop]: [string, any]) => prop.type === 'title',
                  );
                  if (titleProperty) {
                    const titleValue = (titleProperty[1] as any).title;
                    return titleValue
                      ?.map((text: any) => text.plain_text)
                      .join('') || 'Tarefa do Notion';
                  }
                  return 'Tarefa do Notion';
                })()
              ) : (
                'Tarefa do Notion'
              )}
            </DialogTitle>
            {notionLink && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleOpenInNotion}
                className="flex-shrink-0"
              >
                <ExternalLink className="h-4 w-4 mr-2" />
                Abrir no Notion
              </Button>
            )}
          </div>
        </DialogHeader>

        {isLoading ? (
          <div className="space-y-4">
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-5/6" />
            <Skeleton className="h-32 w-full" />
          </div>
        ) : taskDetails ? (
          <>

            <div className="space-y-6">
              {/* Propriedades */}
              {taskDetails.properties && Object.keys(taskDetails.properties).length > 0 && (
                <div className="border-b pb-4">
                  <h3 className="text-sm font-semibold text-foreground mb-3">Propriedades</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {sortProperties(taskDetails.properties).map(([key, value]: [string, any]) =>
                      renderProperty(key, value),
                    )}
                  </div>
                </div>
              )}

              {/* Conteúdo */}
              {taskDetails.content && taskDetails.content.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-foreground mb-3">Conteúdo</h3>
                  <div className="prose prose-sm max-w-none dark:prose-invert">
                    <NotionBlockRenderer blocks={taskDetails.content} />
                  </div>
                </div>
              )}

              {(!taskDetails.content || taskDetails.content.length === 0) &&
                (!taskDetails.properties ||
                  Object.keys(taskDetails.properties).length === 0) && (
                  <div className="text-center py-8 text-muted-foreground">
                    <p>Nenhum conteúdo disponível para esta tarefa.</p>
                  </div>
                )}
            </div>
          </>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

